import { ThemeComponent } from '@/theme/ThemeComponent';
import { OptionSelector } from '@/utils/OptionSelectors';
import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ProductOption } from '@/components/Product/ProductOptionPicker';

interface ValueRadio extends HTMLInputElement {
    dataset: {
        value: string;
    };
}

@Component
export class ProductButtonOption extends ThemeComponent implements ProductOption {
    @Prop name!: string;
    @Prop values!: string[];
    @Prop selected!: string | null;
    @Prop position!: number;
    @Prop({ default: false }) isFrameset!: boolean;

    @Ref buttons!: ValueRadio[];
    @Ref({ required: false }) labelSelectedOption?: HTMLElement;

    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];
    private currentFocusIndex: number = -1;

    private _onChange = new SimpleEvent<string | null>();
    public get onChange() {
        return this._onChange.asEvent();
    }

    init() {
        this.bindButtonEvents();
        this.bindKeyboardEvents();
        this._onChange.dispatch(this.selected);
        this.updateButtonStyles();
        this.setInitialFocus();

        // Set initial label selected value
        if (this.labelSelectedOption && this.selected) {
            this.labelSelectedOption.textContent = this.selected.toLowerCase();
        }
    }

    private setInitialFocus() {
        // Set initial focus to selected option or first available option
        const selectedIndex = this.buttons.findIndex((button) => button.value === this.selected);
        this.currentFocusIndex = selectedIndex >= 0 ? selectedIndex : 0;
    }

    private bindButtonEvents() {
        for (const button of this.buttons) {
            this.$listen(button, 'change', () => {
                if (!button.checked) {return;}

                const value = button.value || null;
                this.select(value);

                // Update label selected value
                if (this.labelSelectedOption && value) {
                    this.labelSelectedOption.textContent = value.toLowerCase();
                }

                this._onChange.dispatch(value);
            });

            // Add focus event listeners to track which button is focused
            this.$listen(button, 'focus', () => {
                const index = this.buttons.indexOf(button);
                if (index >= 0) {
                    this.currentFocusIndex = index;
                }
            });
        }
    }

    private bindKeyboardEvents() {
        // Add keyboard event listener to the container
        const container = this.querySelector('.product-form__button-options-container') as HTMLElement;
        if (container) {
            this.$listen(container, 'keydown', (event) => {
                this.handleKeyDown(event);
            });
        }
    }

    private handleKeyDown(event: KeyboardEvent) {
        const totalOptions = this.buttons.length;
        if (totalOptions === 0) {return;}

        switch (event.key) {
            case 'Enter':
            case ' ': {
                event.preventDefault();
                // Find the currently focused button by checking which one has actual focus
                let focusedButton: ValueRadio | null = null;

                // First try to get the active element
                const activeElement = document.activeElement;

                // Check if active element is one of our radio buttons
                if (activeElement && this.buttons.includes(activeElement as ValueRadio)) {
                    focusedButton = activeElement as ValueRadio;
                } else if (activeElement) {
                    // If active element is not a radio button, it might be a label
                    // Find the associated radio button
                    const label = activeElement as HTMLElement;
                    const forAttribute = label.getAttribute('for');
                    if (forAttribute) {
                        const radioButton = document.getElementById(forAttribute) as ValueRadio;
                        if (radioButton && this.buttons.includes(radioButton)) {
                            focusedButton = radioButton;
                        }
                    }
                }

                // Fallback to currentFocusIndex if we still don't have a focused button
                if (!focusedButton && this.currentFocusIndex >= 0 && this.currentFocusIndex < this.buttons.length) {
                    focusedButton = this.buttons[this.currentFocusIndex];
                }

                if (!focusedButton) {return;}

                // Uncheck all other options
                for (const otherButton of this.buttons) {
                    otherButton.checked = false;
                }

                // Check the focused option
                focusedButton.checked = true;

                // Trigger the existing click/change behavior
                focusedButton.dispatchEvent(new Event('change', { bubbles: true }));
                // Ensure _onChange always fires for add-to-cart
                this._onChange.dispatch(focusedButton.value || null);

                break;
            }
        }
    }

    public select(value: string | null) {
        this.selected = value;
        this.updateButtonStyles();

        // Update label selected value
        if (this.labelSelectedOption && value) {
            this.labelSelectedOption.textContent = value.toLowerCase();
        }

        // Update focus index to match selected value
        const selectedIndex = this.buttons.findIndex((button) => button.value === value);
        if (selectedIndex >= 0) {
            this.currentFocusIndex = selectedIndex;
        }
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock)
            .map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists)
            .map((v) => v.value);

        this.updateButtonStyles();
    }

    public updateButtonStyles() {
        for (const button of this.buttons) {
            const value = button.value;
            const selected = (value || null) === this.selected;
            const outOfStock = this.valuesWithoutStock.includes(value);
            const noVariant = this.valuesWithoutVariant.includes(value);

            if (button.parentNode == null) { return; }

            const wrapper = button.parentNode as HTMLElement;
            if (selected) {
                wrapper.classList.add('product-form__button-item--active');
            } else {
                wrapper.classList.remove('product-form__button-item--active');
            }
            if (noVariant) {
                wrapper.classList.add('product-form__button-item--no-variant');
            } else {
                wrapper.classList.remove('product-form__button-item--no-variant');
            }
            if (outOfStock) {
                wrapper.classList.add('product-form__button-item--out-of-stock');
            } else {
                wrapper.classList.remove('product-form__button-item--out-of-stock');
            }

        }
    }
}
